import { defineMessages } from 'react-intl'

export interface Message {
  id: string
  defaultMessage: string
}

export const messages = defineMessages({
  RESOURCES_AND_SUPPORTING_DOCS: {
    id: 'RESOURCES_AND_SUPPORTING_DOCS',
    defaultMessage: 'Resources & Supporting Documentation',
  },
  EMPROVE_DOSSIERS: {
    id: 'EMPROVE_DOSSIERS',
    defaultMessage: 'Emprove® Dossiers',
  },
  REQUIRES_LOGIN: { id: 'REQUIRES_LOGIN', defaultMessage: 'Requires Login' },
  GS1_STANDARD: { id: 'GS1_STANDARD', defaultMessage: 'GS1 Standard' },
  THE_SMART_LABEL_PAGE: {
    id: 'THE_SMART_LABEL_PAGE',
    defaultMessage: 'the Smart Label page',
  },
  CONTACT_CUSTOMER_SERVICE: {
    id: 'CONTACT_CUSTOMER_SERVICE',
    defaultMessage: 'contact customer service',
  },
  SCAN_NOW_APP_DOWNLOAD_PAGE: {
    id: 'SCAN_NOW_APP_DOWNLOAD_PAGE',
    defaultMessage: 'Scan Now App download page',
  },
  APPLICATION_IDENTIFIERS_ABOUT_GS1: {
    id: 'APPLICATION_IDENTIFIERS_ABOUT_GS1',
    defaultMessage: 'Read more about GS1 here',
  },
  GS1_WEBSITE_HERE: {
    id: 'GS1_WEBSITE_HERE',
    defaultMessage: ' GS1 website here',
  },
  GS1_STANDARD_HERE: {
    id: 'GS1_STANDARD_HERE',
    defaultMessage: 'GS1 standard here',
  },
  RESULTS: { id: 'RESULTS', defaultMessage: 'results' },
  SDS: { id: 'SDS', defaultMessage: 'SDS' },
  DOCUMENT_FOR_LOT_NUMBER: {
    id: 'DOCUMENT_FOR_LOT_NUMBER',
    defaultMessage: '{docType} for Lot Number {lotNumber}',
  },
  HOW_TO_USE_SCAN_NOW: {
    id: 'HOW_TO_USE_SCAN_NOW',
    defaultMessage: 'How To Use Scan Now',
  },
  WHAT_IS_SCAN_NOW: {
    id: 'WHAT_IS_SCAN_NOW',
    defaultMessage: 'What is Scan Now?',
  },
  SCAN_NOW_EXPLANATION: {
    id: 'SCAN_NOW_EXPLANATION',
    defaultMessage:
      'The Scan Now platform helps you make the most of {SmartLabel} product barcodes. Here is how it works: scan a barcode to retrieve product data, including the Item Code, Batch Number, Shelf Life, and Global Trade Item Number (GTIN). The information stored in the barcode will be displayed, along with links to documents such as Certificates of Analysis (CoAs), and Safety Data Sheets (SDSs), when appropriate.',
  },
  SCAN_NOW_SMART_LABEL: {
    id: 'SCAN_NOW_SMART_LABEL',
    defaultMessage: 'Smart Label',
  },
  SCAN_NOW_INSTRUCTIONS: {
    id: 'SCAN_NOW_INSTRUCTIONS',
    defaultMessage:
      'To scan your barcode, please use a standard barcode reader that supports {barcodes}. For best results, please set up your scanner to use an English keyboard layout.',
  },
  SAMPLE_SMART_LABEL: {
    id: 'SAMPLE_SMART_LABEL',
    defaultMessage: 'Sample Smart Label',
  },
  TRY_THE_SCAN_NOW_MOBILE_APP: {
    id: 'TRY_THE_SCAN_NOW_MOBILE_APP',
    defaultMessage: 'Try the Scan Now Mobile App',
  },
  SCAN_NOW_MOBILE_APP_INFO: {
    id: 'SCAN_NOW_MOBILE_APP_INFO',
    defaultMessage:
      'Scan our Smart Labels with your camera-equipped mobile device.',
  },
  SCAN_NOW_MOBILE_AVAILABILITY: {
    id: 'SCAN_NOW_MOBILE_AVAILABILITY',
    defaultMessage: 'ScanNow is available on {iOS} and as an {AndroidBeta}.',
  },
  IOS: { id: 'IOS', defaultMessage: 'iOS' },
  ANDROID_BETA: { id: 'ANDROID_BETA', defaultMessage: 'Android Beta' },
  OK: { id: 'OK', defaultMessage: 'OK' },
  SCAN_NOW_IS_CURRENTLY_OFFLINE: {
    id: 'SCAN_NOW_IS_CURRENTLY_OFFLINE',
    defaultMessage: 'Scan Now is currently offline.',
  },
  SCAN_NOW_OFFLINE_INFO: {
    id: 'SCAN_NOW_OFFLINE_INFO',
    defaultMessage: 'For more information visit our {SmartLabelPage}.',
  },
  SMART_LABEL_PAGE: {
    id: 'SMART_LABEL_PAGE',
    defaultMessage: 'Smart Label page',
  },
  SCAN_NOW_ERROR: {
    id: 'SCAN_NOW_ERROR',
    defaultMessage:
      'There are no valid AIs to parse. Try scanning again. If the error persists, please contact Customer Service',
  },
  UPDATING: { id: 'UPDATING', defaultMessage: 'Updating...' },
  APPLICATION_IDENTIFIERS_ABOUT: {
    id: 'APPLICATION_IDENTIFIERS_ABOUT',
    defaultMessage: 'About Application Identifiers (AI)',
  },
  APPLICATION_IDENTIFIERS_LINE1: {
    id: 'APPLICATION_IDENTIFIERS_LINE1',
    defaultMessage:
      'MilliporeSigma Smart Labels follow the barcode guidelines defined by GS1, a global not-for-profit standards group. {link}.',
  },
  APPLICATION_IDENTIFIERS_LINE2: {
    id: 'APPLICATION_IDENTIFIERS_LINE2',
    defaultMessage:
      'The specification includes rules for how to <b>format the product data</b> contained in the barcode. Those rules include Application Identifiers (AIs), which are <b>prefixes</b> that are paired with the product information itself, such as the expiration date, for example.',
  },
  APPLICATION_IDENTIFIERS_LINE3: {
    id: 'APPLICATION_IDENTIFIERS_LINE3',
    defaultMessage:
      'When it is possible, GS1 standardizes the data fields themselves, too.\n For instance, the aforementioned expiration date is AI code 17; the GS1 rules say that the date is six digits long,\n two digits for the year, two for the month, and two for the date. So a full example Expiry Date would be 17250128 where ‘17’ is the AI, ‘25’ is the year (2025), ‘01’ is the month (January), and ‘28’ is the date.',
  },
  APPLICATION_IDENTIFIERS_LINE4: {
    id: 'APPLICATION_IDENTIFIERS_LINE4',
    defaultMessage:
      'There are hundreds of AIs, including GTIN (AI <b>01</b>), USE BY OR EXPIRY (AI <b>17</b>), BATCH/LOT (AI <b>10</b>), SERIAL (AI <b>21</b>), and ORIGIN (AI <b>422</b>).',
  },
  APPLICATION_IDENTIFIERS_LINE5: {
    id: 'APPLICATION_IDENTIFIERS_LINE5',
    defaultMessage:
      'Read more about AIs on the {gs1Website} and the {gs1Standard}.',
  },
  APPLICATION_IDENTIFIERS_LINE6: {
    id: 'APPLICATION_IDENTIFIERS_LINE6',
    defaultMessage:
      'The Scan Now webpage reads the long string of numbers and letters that make up a Smart Label barcode, finds the AIs and product data and displays the information in a table.',
  },
  SCAN_NOW_FAQ: { id: 'SCAN_NOW_FAQ', defaultMessage: 'Scan Now FAQ' },
  SCAN_NOW_NOTICE: {
    id: 'SCAN_NOW_NOTICE',
    defaultMessage: 'To proceed, scan a valid product barcode.',
  },
  HOW_DO_I_DO_THIS: {
    id: 'HOW_DO_I_DO_THIS',
    defaultMessage: 'How do I do this?',
  },
  FAQ: { id: 'FAQ', defaultMessage: 'FAQ' },
  SUBMIT: { id: 'SUBMIT', defaultMessage: 'Submit' },
  RESET: { id: 'RESET', defaultMessage: 'Reset' },
  SCAN_NOW_FAQ_QUESTION_1: {
    id: 'SCAN_NOW_FAQ_QUESTION_1',
    defaultMessage:
      '#1: On which product labels will the 2D barcodes be implemented?',
  },
  SCAN_NOW_FAQ_ANSWER_1: {
    id: 'SCAN_NOW_FAQ_ANSWER_1',
    defaultMessage:
      'First, we will implement the labels on our Emprove® Chemicals, followed by a continuous expansion to the entire Life Science product portfolio (with the exception of products with small labels).',
  },
  SCAN_NOW_FAQ_QUESTION_2: {
    id: 'SCAN_NOW_FAQ_QUESTION_2',
    defaultMessage:
      '#2: Will the information on the 2D barcode be removed from the labels?',
  },
  SCAN_NOW_FAQ_ANSWER_2: {
    id: 'SCAN_NOW_FAQ_ANSWER_2',
    defaultMessage:
      "No, all information stored in the 2D barcode will stay 'human readable' on the labels.",
  },
  SCAN_NOW_FAQ_QUESTION_3: {
    id: 'SCAN_NOW_FAQ_QUESTION_3',
    defaultMessage: '#3: Do you plan to put any other data on the 2D barcodes?',
  },
  SCAN_NOW_FAQ_ANSWER_3: {
    id: 'SCAN_NOW_FAQ_ANSWER_3',
    defaultMessage:
      'Yes, very likely and we will inform you soon after updates are made.',
  },
  SCAN_NOW_FAQ_QUESTION_4: {
    id: 'SCAN_NOW_FAQ_QUESTION_4',
    defaultMessage:
      '#4: Do I need any special technical equipment to scan a 2D barcode?',
  },
  SCAN_NOW_FAQ_ANSWER_4: {
    id: 'SCAN_NOW_FAQ_ANSWER_4',
    defaultMessage:
      'Yes, please use a scanning device which is able to read <b>Data Matrix 2D</b> barcodes that adhere to the {GS1Standard}. {linebreak} Important note - Do not confuse 1D Data Matrix GS1 barcode scanners with scanners able to read <b>2D</b> Data Matrix GS1 barcodes.',
  },
  SCAN_NOW_FAQ_QUESTION_5: {
    id: 'SCAN_NOW_FAQ_QUESTION_5',
    defaultMessage: '#5: Will 1D barcodes be removed from the label?',
  },
  SCAN_NOW_FAQ_ANSWER_5: {
    id: 'SCAN_NOW_FAQ_ANSWER_5',
    defaultMessage:
      'Not immediately. After internal and customer systems/processes have been updated, the 1D barcodes will likely be removed.',
  },
  SCAN_NOW_FAQ_QUESTION_6: {
    id: 'SCAN_NOW_FAQ_QUESTION_6',
    defaultMessage:
      '#6: Where can I learn more about the 2D barcodes on Emprove® product labels?',
  },
  SCAN_NOW_FAQ_ANSWER_6: {
    id: 'SCAN_NOW_FAQ_ANSWER_6',
    defaultMessage: 'For more information, visit {TheSmartLabelPage}.',
  },
  SCAN_NOW_FAQ_QUESTION_7: {
    id: 'SCAN_NOW_FAQ_QUESTION_7',
    defaultMessage:
      '#7: Are there any scanner configuration recommendations for the Scan Now page?',
  },
  SCAN_NOW_FAQ_ANSWER_7: {
    id: 'SCAN_NOW_FAQ_ANSWER_7',
    defaultMessage:
      'For best results, please set up your scanner to use an English keyboard layout. \n If you encounter any issues, please {contactCustomerService}.',
  },
  SCAN_NOW_FAQ_QUESTION_8: {
    id: 'SCAN_NOW_FAQ_QUESTION_8',
    defaultMessage: '#8: Are there plans to create a Scan Now App?',
  },
  SCAN_NOW_FAQ_ANSWER_8: {
    id: 'SCAN_NOW_FAQ_ANSWER_8',
    defaultMessage:
      'Yes. Please see the {ScanNowAppDownloadPage} for more information.',
  },
  SCAN_NOW_FAQ_QUESTION_9: {
    id: 'SCAN_NOW_FAQ_QUESTION_9',
    defaultMessage: '#9: What is an AI (Application Identifier)?',
  },
})

export default messages
